// @ts-check
"use strict"

import { parseReportJson } from "../../utils/report-parser"

const REQUIRED_FIELDS = ["findings", "impression", "conclusion"]

/**
 * @param {string} text
 */
export function parseJsonToFields(text = "") {
  if(text.trim() === "") return []

  let parsed = parseReportJson(text)
  if(parsed == null) return []

  // if(isEmptyReport(parsed)) throw new Error("Empty report")

  let fieldList = Object.entries(parsed)

  return fieldList.map(([label, value]) => {
    return {
      label,
      value,
      required: REQUIRED_FIELDS.includes(label.toLowerCase())
    }
  })
}

/** @param {import(".").ReportField[]} reportFields */
export function parseFieldsToMarkdown(reportFields) {
  return reportFields.map(field => {
    return `### ${field.label}\n${fieldValueToMarkdown(field.value, null, {shouldFormatKey: true})}`
  }).join("\n")
}

/**
  * @param {import(".").ReportFieldValue} value
  * @param {string} [key]
  * @param {Object} options
  * @param {number} [options.depth]
  * @param {boolean} [options.shouldFormatKey]
  */
export function fieldValueToMarkdown(value, key, {depth = 0, shouldFormatKey = false} = {}) {
  const transformedValue = (() => {
    if(value == null) return ""
    if(Array.isArray(value) && value.length === 0) return ""
    if(Array.isArray(value) && value.length === 1) return value[0]
    return value
  })();

  let output = ""
  if(key != null) {
    output += `${getIndentation(depth)}- `
    if(shouldFormatKey) output += `**${key}:**`
    else output += `${key}:` 
  }

  const nextDepth = key != null ? depth + 1: depth

  if(typeof transformedValue === "string") {
    if(key != null) output += " "
    output += transformedValue
    return output
  }

  if(Array.isArray(transformedValue)) {
    if(key != null) output += "\n"
    output += (
      transformedValue.map(item => `${getIndentation(nextDepth)}- ${item ?? ""}`)
        .join(getVerticalSpacing(nextDepth))
    )
    return output
  }


  if(typeof transformedValue === "object") {
    if(key != null) output += "\n"

    output += (
      Object.entries(transformedValue).map(([entryKey, entryValue]) => (
        `${fieldValueToMarkdown(entryValue, entryKey, {depth: nextDepth})}`
      )).join(getVerticalSpacing(nextDepth))
    )
    return output
  }

  return null
}

/** @param {number} depth */
function getIndentation(depth) {
  return "  ".repeat(depth * 2)
}

/** @param {number} depth */
function getVerticalSpacing(depth) {
  if(depth === 0) return "\n\n"
  return "\n"
}

/** @param {string} report */
export function parseJsonToMarkdown(report = "") {
  if(report === "") return ""
  const reportFields = parseJsonToFields(report)
  return parseFieldsToMarkdown(reportFields)
}

/**
 * @param {string} markdown
 * @returns {import(".").ReportFieldValue}
 */
export function markdownToFieldValue(markdown) {
  let parsedLines = processMarkdown(markdown)
  let value = getFieldValueFromParsedLines(parsedLines)
  return value.result
}

/**
 * @param {string} markdown
 */
function processMarkdown(markdown) {
  let lines = markdown.split('\n')
  if(lines.length === 0) return null

  /** @type {ProcessedMarkdownLine[]} */
  let processedLines = lines.map(line => {
    let depth = line.match(/^\s*/)[0].length / 4
    let trimmedLine = line.slice(depth * 4)

    let isStringValue = !trimmedLine.startsWith("-"),
        isKeyValuePair = KEY_VALUE_PAIR_REGEXP.test(trimmedLine),
        isListItem = LIST_ITEM_REGEXP.test(trimmedLine) && !isKeyValuePair

    if(isStringValue) {
      return {
        type: "string",
        value: trimmedLine,
        depth,
      }
    }

    if(isListItem) {
      return {
        type: "list-item",
        value: trimmedLine.match(LIST_ITEM_REGEXP)[1],
        depth,
      }
    }

    if(isKeyValuePair) {
      let match = trimmedLine.match(KEY_VALUE_PAIR_REGEXP)
      return {
        type: "key-value",
        key: match[1],
        value: match[2].trim(),
        depth,
      }
    }
  })

  return processedLines
}

/**
  * @typedef {
      | { type: "string", value: string, depth: number }
      | { type: "list-item", value: string, depth: number }
      | { type: "key-value", key: string, value: string, depth: number }
    } ProcessedMarkdownLine
  */

/** 
  * @param {ProcessedMarkdownLine[]} lines
  * @param {number} [startIndex]
  * @param {number} [depth]
  */
function getFieldValueFromParsedLines(lines, startIndex = 0, depth = 0) {
  let numberOfLines = lines.length
  
  // Skip empty lines
  if(lines[startIndex].type === "string" && lines[startIndex].value.trim() === "") {
    return getFieldValueFromParsedLines(lines, startIndex + 1, depth)
  }

  /** @type {import(".").ReportFieldValue} */
  let result,
      startLine = lines[startIndex],
      currentIndex = startIndex

  if(startLine.type === "string") {
    result = (
      lines.slice(startIndex).map(line => {
        let indentation = getIndentation(line.depth)
        if(line.type === "string") return `${indentation}${line.value}`
        if(line.type === "list-item") return `${indentation}- ${line.value}`
        if(line.type === "key-value") return `${indentation}- ${line.key}: ${line.value}`
      }).join("\n")
    )
    currentIndex = lines.length - 1
  }

  else if(startLine.type === "list-item") {
    if(startLine.depth !== depth) {
      throw new MarkdownFormatError(
        `Expected list item to be at depth ${depth}, got ${startLine.depth}`
      )
    }
  
    result = [startLine.value]
    /** @type {ProcessedMarkdownLine} */
    let prevLine = startLine,
        nextLine

    for(let i = startIndex + 1; i < numberOfLines; i++) {
      nextLine = lines[i]
      if(nextLine.type === "string" && nextLine.value.trim() === "") continue

      if(nextLine.depth > prevLine.depth) {
        throw new MarkdownFormatError("List items cannot be nested")
      }
      if(nextLine.depth < prevLine.depth && i !== startIndex) break

      currentIndex = i

      if(nextLine.type === "list-item") result.push(nextLine.value)
      if(nextLine.type === "key-value") result.push(`${nextLine.key}: ${nextLine.value}`)
      if(nextLine.type === "string") {
        throw new MarkdownFormatError("Expected list item but got string value")
      }

      prevLine = nextLine
    }
  }

  else if(startLine.type === "key-value") {
    if(startLine.depth !== depth) {
      throw new MarkdownFormatError(
        `Expected key-value pair to be at depth ${depth}, got ${startLine.depth}`
      )
    }

    result = { [startLine.key]: startLine.value }
    /** @type {ProcessedMarkdownLine & {type: "key-value"}} */
    let prevLine = startLine,
        nextLine,
        i = startIndex + 1
      
    while(i < numberOfLines) {
      nextLine = lines[i]
      if(nextLine.type === "string" && nextLine.value.trim() === "") {
        i++; continue
      }

      if(nextLine.type === "string") {
        throw new MarkdownFormatError("Expected key-value pair but got string value")
      }
      if(nextLine.type === "list-item" && nextLine.depth === prevLine.depth) {
        throw new MarkdownFormatError("Expected key-value pair but got list item")
      }

      if(nextLine.depth < prevLine.depth && i === startIndex) {
        throw new MarkdownFormatError("Expected nested value but got end of object")
      }
      if(nextLine.depth < prevLine.depth && i !== startIndex) break
      if(nextLine.depth > prevLine.depth && result[prevLine.key] != "") {
        throw new MarkdownFormatError(
          "Expected current key to have empty value to set nested value"
        )
      }

      currentIndex = i

      if(nextLine.depth > prevLine.depth && result[prevLine.key] == "") {
        let parsedValue = getFieldValueFromParsedLines(lines, i, prevLine.depth + 1)
        result[prevLine.key] = parsedValue.result
        i = parsedValue.index
        currentIndex = i - 1
        continue
      }

      result[nextLine.key] = nextLine.value
      prevLine = nextLine
      i++
    }
  }

  else throw new MarkdownFormatError("Invalid parsed line type")

  return {
    result: result ?? null,
    index: currentIndex + 1,
  }
}

class MarkdownFormatError extends Error {
  /**
   * @param {string} [message]
   * @param {ErrorOptions} [options]
   */
  constructor(message, options) {
    super(message, options)
    this.name = "MarkdownFormatError"
  }
}

const LIST_ITEM_REGEXP = /^\s*-\s(.+)$/
const KEY_VALUE_PAIR_REGEXP = /^\s*-\s(.+):(.*)$/

/** @param {string} report */
function isEmptyReport(report) {
  console.log('parsed report', report)
  if(Object.entries(report).length === 0) return true
  let parsed = parseReportJson(report)
  return isEmptyReportValue(parsed)
}

/**
  * @param {*} value
  * @returns {boolean}
  */
function isEmptyReportValue(value) {
  if(value == null) return true
  if(typeof value === "number") return false
  if(typeof value === "string") return value.trim() === ""
  if(Array.isArray(value)) return !value.some(item => !isEmptyReportValue(item))
  if(typeof value === "object") return isEmptyReportValue(Object.values(value))

  throw new Error("Invalid value type")
}
